import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '../../models/api.model';
import { AuthStore } from '../../../core/stores/auth.store';
import { EmailTemplate } from '../models/email.model';
import type { OrderConfirmationImageField } from '../models/order-confirmation-image-field';

@Injectable({
  providedIn: 'root',
})
export class EmailsService {
  private http = inject(HttpClient);
  private authStore = inject(AuthStore);

  private apiRoot = '/bo/emails';

  private get loggedUser(): { fullName: string; email: string } {
    const user = this.authStore.user();

    if (!user) {
      throw new Error('Emails actions require an authenticated session');
    }

    return user;
  }

  public get$(
    templateName = 'orderConfirmation'
  ): Observable<ApiResponse<EmailTemplate>> {
    return this.http.get<ApiResponse<EmailTemplate>>(
      environment.apiURl + `${this.apiRoot}/${templateName}`
    );
  }

  public update$(
    data: EmailTemplate,
    templateName = 'orderConfirmation'
  ): Observable<ApiResponse<EmailTemplate>> {
    const emailData = data.emailData ?? {};
    const payload = {
      isActive: data.isActive,
      emailData: {
        legalText: emailData['legalText'] as string | undefined,
        greetingText: emailData['greetingText'] as string | undefined,
      },
    };

    return this.http.patch<ApiResponse<EmailTemplate>>(
      environment.apiURl + `${this.apiRoot}/${templateName}`,
      payload
    );
  }

  public deleteImage$(
    templateName: string,
    fieldName: 'footerBanner'
  ): Observable<ApiResponse<EmailTemplate>> {
    return this.http.delete<ApiResponse<EmailTemplate>>(
      environment.apiURl + `${this.apiRoot}/image`,
      {
        body: {
          templateName,
          fieldName,
        },
      }
    );
  }

  public getPreview$(
    clientName: string,
    templateName = 'orderConfirmation'
  ): Observable<ApiResponse<{ template: string }>> {
    return this.http.get<ApiResponse<{ template: string }>>(
      environment.apiURl +
        `${this.apiRoot}/preview/${templateName}?patronName=${this.loggedUser.fullName}&clientName=${clientName}`
    );
  }

  public sendDemoEmail$(
    templateName = 'orderConfirmation'
  ): Observable<ApiResponse<{ email: string }>> {
    return this.http.post<ApiResponse<{ email: string }>>(
      environment.apiURl + `${this.apiRoot}/send/test`,
      {
        templateName,
        sendTo: this.loggedUser.email,
        patronName: this.loggedUser.fullName,
      }
    );
  }

  public uploadImage$(
    templateName: string,
    fieldName: OrderConfirmationImageField,
    image: File
  ): Observable<ApiResponse<{ imageUrl: string }>> {
    const formData = new FormData();
    formData.append('templateName', templateName);
    formData.append('fieldName', fieldName);
    formData.append('image', image);

    return this.http.post<ApiResponse<{ imageUrl: string }>>(
      environment.apiURl + `${this.apiRoot}/upload-image`,
      formData
    );
  }
}
